/**
 * Realtime session-detail channels / events.
 *
 * Backend contract (public Laravel Echo channel):
 *   session-details.{sessionId}
 *     session.collaborator.updated   — collaborator joined with working note
 *     session.collaborator.left      — collaborator left with exit note
 *     session.collaborator.reaction  — like/heart impression added or changed
 *     session.collaborator.reaction.removed — impression removed (optional)
 *
 * Override with env if the backend ever changes the template:
 *   REACT_APP_PUSHER_SESSION_DETAIL_CHANNEL=session-details.{sessionId}
 */

import {
  eventNameAliases,
  unwrapRealtimePayload,
} from "./messagesRealtime"

export const SESSION_COLLABORATOR_UPDATED_EVENT =
  "session.collaborator.updated"
export const SESSION_COLLABORATOR_LEFT_EVENT = "session.collaborator.left"
export const SESSION_COLLABORATOR_REACTION_EVENT =
  "session.collaborator.reaction"
export const SESSION_COLLABORATOR_REACTION_REMOVED_EVENT =
  "session.collaborator.reaction.removed"

export const SESSION_COLLABORATOR_UPDATED_EVENTS = eventNameAliases(
  SESSION_COLLABORATOR_UPDATED_EVENT,
)
export const SESSION_COLLABORATOR_LEFT_EVENTS = eventNameAliases(
  SESSION_COLLABORATOR_LEFT_EVENT,
)
export const SESSION_COLLABORATOR_REACTION_EVENTS = eventNameAliases(
  SESSION_COLLABORATOR_REACTION_EVENT,
)
export const SESSION_COLLABORATOR_REACTION_REMOVED_EVENTS = eventNameAliases(
  SESSION_COLLABORATOR_REACTION_REMOVED_EVENT,
)

export const getSessionDetailChannelName = sessionId => {
  if (sessionId == null || sessionId === "") return ""
  const template = process.env.REACT_APP_PUSHER_SESSION_DETAIL_CHANNEL
  const base = template
    ? String(template)
        .replace(/\{sessionId\}/g, String(sessionId))
        .replace(/\{id\}/g, String(sessionId))
    : `session-details.${sessionId}`
  return String(base || "").replace(/^(private-|presence-)/, "")
}

const asObject = value =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null

export const matchesSessionRealtime = (sessionId, payloadSessionId) => {
  if (payloadSessionId == null || payloadSessionId === "") return true
  return String(payloadSessionId) === String(sessionId)
}

export const parseSessionCollaboratorPayload = payload => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return null

  const nested =
    asObject(data.collaborator) ||
    asObject(data.session_collaborator) ||
    asObject(data.member)
  const collaborator = nested || (data.id != null ? data : null)
  if (!collaborator || (collaborator.id == null && collaborator.user_id == null)) {
    return null
  }

  const user =
    asObject(collaborator.user) ||
    asObject(collaborator.sender) ||
    asObject(data.user) ||
    asObject(data.sender)

  return {
    sessionId:
      data.session_id ??
      data.sessionId ??
      collaborator.session_id ??
      null,
    collaborator: {
      ...collaborator,
      user: asObject(collaborator.user) || user,
      user_id: collaborator.user_id || user?.id || null,
    },
  }
}

/**
 * Parse session.collaborator.reaction payloads:
 * {
 *   reaction_id, session_collaborator_id, session_id, user_id,
 *   reaction, reaction_summary, sender
 * }
 */
export const parseSessionCollaboratorReactionPayload = (
  payload,
  { removed = false } = {},
) => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return null

  const collaboratorId =
    data.session_collaborator_id ||
    data.collaborator_id ||
    data.sessionCollaboratorId ||
    data.collaboratorId ||
    asObject(data.collaborator)?.id ||
    asObject(data.session_collaborator)?.id ||
    ""
  if (collaboratorId == null || collaboratorId === "") return null

  const sender = asObject(data.sender) || asObject(data.user)
  const userId =
    data.user_id != null
      ? String(data.user_id)
      : data.userId != null
        ? String(data.userId)
        : sender?.id != null
          ? String(sender.id)
          : ""

  const reaction = String(data.reaction || data.emoji || "").trim()
  const reactionSummary = Array.isArray(data.reaction_summary)
    ? data.reaction_summary
    : Array.isArray(data.reactions)
      ? data.reactions
      : null

  const isRemoved =
    removed ||
    data.removed === true ||
    String(data.action || data.type || "")
      .toLowerCase()
      .includes("remov") ||
    (!reaction &&
      Array.isArray(reactionSummary) &&
      reactionSummary.length === 0)

  return {
    sessionId: data.session_id ?? data.sessionId ?? null,
    collaboratorId: String(collaboratorId),
    userId,
    reaction: isRemoved ? "" : reaction,
    reactionSummary,
    removed: isRemoved,
    reactionId: data.reaction_id || data.reactionId || null,
    sender,
  }
}
